import type { PlannerContext } from "../../api/routing";
import { EMPTY_SITE_FILTER, type SiteFilterQuery } from "../../api/sites";
import { Pill } from "../../components/ui";

type Flag = keyof Pick<SiteFilterQuery, "assigned" | "flagged" | "red_list" | "has_qcp" | "no_qcp" | "located" | "not_located" | "not_done">;

/** The two switches a planner reaches for every time; the rest sit behind
 * "More filters". Each is the site list's own filter, so a planner opened from
 * the Sites screen shows the same ticks it was sent with. */
const FRONT_FLAGS: readonly [Flag, string][] = [
  ["located", "Located only"],
  ["red_list", "Red List only"],
];
const MORE_FLAGS: readonly [Flag, string][] = [
  ["assigned", "Assigned"],
  ["flagged", "Flagged"],
  ["has_qcp", "Has QCP"],
  ["no_qcp", "No QCP"],
  ["not_located", "Not located"],
];

/** Step 1, which sites: the statuses as pills, cluster and crew, and the
 * sites the planner was opened with (from the Sites screen's ticks), each of
 * which can be left out. The site list orders the Red List first already. */
export default function SitesStep({
  context,
  filter,
  onFilter,
  siteIds,
  onSiteIds,
}: {
  context: PlannerContext;
  filter: SiteFilterQuery;
  onFilter: (next: SiteFilterQuery) => void;
  siteIds: readonly number[];
  onSiteIds: (next: number[]) => void;
}) {
  function set<K extends keyof SiteFilterQuery>(key: K, value: SiteFilterQuery[K]) {
    onFilter({ ...filter, [key]: value });
  }
  function toggleStatus(value: string) {
    set("status", filter.status.includes(value) ? filter.status.filter((s) => s !== value) : [...filter.status, value]);
  }
  const flag = ([key, label]: [Flag, string]) => (
    <label key={key} className="check">
      <input type="checkbox" checked={filter[key] !== ""} onChange={(e) => set(key, e.target.checked ? "on" : "")} /> {label}
    </label>
  );
  // The sites still owed a visit, with how many there are, so the planner
  // can route them with everything else.
  const frontFlags: readonly [Flag, string][] = [...FRONT_FLAGS, ["not_done", `Visit not done (${context.visit_not_done})`]];
  const chips = context.selection.chips;
  return (
    <>
      {chips.length > 0 ? (
        <div className="selection-chips" role="group" aria-label="Selected sites">
          {chips.map((chip, index) => {
            const siteId = context.selection.site_ids[index];
            const on = siteIds.includes(siteId);
            return (
              <Pill key={`${chip.value}-${index}`} on={on} onToggle={() => onSiteIds(on ? siteIds.filter((id) => id !== siteId) : [...siteIds, siteId])}>
                {chip.value}
              </Pill>
            );
          })}
          <button type="button" className="row-button" onClick={() => onSiteIds([])}>
            Clear the selection ({siteIds.length})
          </button>
        </div>
      ) : null}
      <div className="pill-row" role="group" aria-label="Filter by status">
        {context.filters.statuses.map((s) => (
          <Pill key={s.value} on={filter.status.includes(s.value)} onToggle={() => toggleStatus(s.value)}>
            {s.label}
          </Pill>
        ))}
      </div>
      <div className="step-fields">
        <label className="field">
          Cluster
          <select value={filter.cluster_id} onChange={(e) => set("cluster_id", e.target.value)}>
            <option value="">All</option>
            {context.filters.clusters
              .filter((c) => filter.contract_id === "" || String(c.contract_id) === filter.contract_id)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </label>
        <label className="field">
          Provider
          <select value={filter.provider_id} onChange={(e) => set("provider_id", e.target.value)}>
            <option value="">All</option>
            {context.filters.providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="step-checks">{frontFlags.map(flag)}</div>
      <details className="step-more">
        <summary>More filters</summary>
        <div className="step-fields">
          <label className="field">
            Contract
            <select value={filter.contract_id} onChange={(e) => onFilter({ ...filter, contract_id: e.target.value, cluster_id: "" })}>
              <option value="">All</option>
              {context.filters.contracts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Valve make
            <input value={filter.valve_make} onChange={(e) => set("valve_make", e.target.value)} />
          </label>
          <label className="field">
            DN from
            <input inputMode="numeric" value={filter.dn_min} onChange={(e) => set("dn_min", e.target.value)} />
          </label>
          <label className="field">
            DN to
            <input inputMode="numeric" value={filter.dn_max} onChange={(e) => set("dn_max", e.target.value)} />
          </label>
        </div>
        <div className="step-checks">{MORE_FLAGS.map(flag)}</div>
        <button type="button" className="row-button" onClick={() => onFilter({ ...EMPTY_SITE_FILTER })}>
          Clear filters
        </button>
      </details>
    </>
  );
}
